import { Page, Text, View } from '@react-pdf/renderer'
import { Card, Progress, Typography } from 'antd'
import { PageFooter } from 'features/reporting/pdfGeneration/components/shared/PageFooter'
import { PageHeader } from 'features/reporting/pdfGeneration/components/shared/PageHeader'
import { PDFStyles } from 'features/reporting/pdfGeneration/styles/PDFStyles'
import { PdfProps, ReportSection } from 'features/reporting/types'
import { useAllWeldScanFiles } from 'pages/reporting/reports/raut/hooks/useAllWeldScanFiles'
import { useRautReportOps } from 'pages/reporting/reports/raut/RautReportProvider'
import { useUnitSystem } from 'pages/reporting/shared/contexts/UnitSystemContext'
import React, { useMemo } from 'react'
import styled from 'styled-components'
import { GeckoMeasurement, GeckoMeasurementUnit, GeckoSource } from 'types'

import { WeldIndicationsView } from './subSections/weldIndicationComponents/WeldIndicationsView'
import { WeldIndicationsTable } from './subSections/weldScanComponents/WeldIndicationsTable'
import { WeldIndicationVisualizations } from './subSections/weldScanComponents/WeldIndicationVisualizations'
import { WeldScanSummaryTable } from './subSections/weldScanComponents/WeldScanSummaryTable'

const AntText = Typography.Text

const StatusCard = styled(Card)`
  && {
    margin-bottom: 1.25rem;
    background: ${({ theme }) =>
      theme.forecast.cardBackground || theme.forecast.background1};
    border: 1px solid
      ${({ theme }) =>
        theme.forecast.cardDividerBackground || theme.forecast.border};
    border-radius: 0.75rem;
    box-shadow: 0 0.125rem 0.5rem
      ${({ theme }) => theme.forecast.boxShadow || 'rgba(0, 0, 0, 0.06)'};

    .ant-card-body {
      padding: 1.5rem;
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }
  }
`

const StatusText = styled(AntText)`
  && {
    color: ${({ theme }) => theme.forecast.secondaryText};
    font-size: 0.875rem;
    line-height: 1.5;
  }
`

const RenderReact: React.FC = () => {
  const {
    formData,
    metadataEntryId,
    flatWeldIndications,
    isLoadingWeldIndications,
    hasWeldIndications,
    handleWeldScanDataFinish,
    handleSaveWeldIndicationGraph,
    handleDeleteWeldIndicationGraph,
    inspections,
  } = useRautReportOps()

  // Get the phased array inspection ID
  const pautInspectionId = useMemo(() => {
    const pautInspection = inspections.find(
      (insp) => insp.data.inspection_type === GeckoSource.PhasedArray.id
    )
    return pautInspection?.id
  }, [inspections])

  // Fetch all weld scan file names
  const { scanFileNames, isLoading: isLoadingScanFiles } =
    useAllWeldScanFiles(pautInspectionId)

  const savedWeldIndicationIds = useMemo(() => {
    const ids = new Set<string>()
    if (formData.savedWeldIndicationGraphs) {
      Object.keys(formData.savedWeldIndicationGraphs).forEach((id) => {
        ids.add(id)
      })
    }
    return ids
  }, [formData.savedWeldIndicationGraphs])

  if (isLoadingWeldIndications || isLoadingScanFiles) {
    return (
      <StatusCard>
        <StatusText>Loading weld scan data...</StatusText>
        <Progress status='active' percent={50} />
      </StatusCard>
    )
  }

  // Show weld indications view if we have scan files or indications
  if (scanFileNames.length > 0 || hasWeldIndications) {
    return (
      <WeldIndicationsView
        weldIndications={flatWeldIndications}
        allScanFileNames={scanFileNames}
        inspectionId={pautInspectionId}
        metadataEntryId={metadataEntryId}
        savedWeldIndicationIds={savedWeldIndicationIds}
        onSaveIndication={handleSaveWeldIndicationGraph}
        onDeleteIndication={handleDeleteWeldIndicationGraph}
        weldScanData={formData.weldScanData}
        onWeldScanDataFinish={handleWeldScanDataFinish}
      />
    )
  }

  return (
    <StatusCard>
      <StatusText>
        No weld scan files found. Upload scan data to populate this section.
      </StatusText>
    </StatusCard>
  )
}

const RenderPDF: React.FC<PdfProps> = ({ onRender }) => {
  const {
    formData,
    hasPhasedArrayInspection,
    flatWeldIndications,
    weldIndicationsWithImages,
    renderedWeldImageUrls,
  } = useRautReportOps()

  const { getGeometricUnit } = useUnitSystem()
  const geometricUnit = getGeometricUnit()

  const scanFileNames = useMemo(() => {
    return formData.weldScanData?.scans.map((scan) => scan.fileName) || []
  }, [formData.weldScanData])

  const fileNamesWithoutIndications = useMemo(() => {
    return scanFileNames.filter((fileName) => {
      return !flatWeldIndications.some(
        (ind) => ind.customInfo.file_name === fileName
      )
    })
  }, [scanFileNames, flatWeldIndications])

  const scansWithIndications = useMemo(() => {
    if (!formData.weldScanData) return []

    return formData.weldScanData.scans.map((scan) => {
      const scanIndications = flatWeldIndications.filter(
        (indication) => indication.customInfo.file_name === scan.fileName
      )

      const mappedIndications = scanIndications.map((indication, index) => {
        const loc = indication.customInfo.location

        const start = loc?.startZ || 0 // meters
        const length = loc ? loc.endZ - loc.startZ : 0 // meters
        const axialPosition = loc ? (loc.startX + loc.endX) / 2 : 0 // meters
        const depth = loc?.endY || 0 // meters
        const height = loc ? loc.startY - loc.endY : 0 // meters
        const amplitude = indication.customInfo.max_amplitude || 0

        return {
          indicationNumber: index + 1,
          start: GeckoMeasurement.CreateFromValue(
            start,
            GeckoMeasurementUnit.Meters
          ).convertTo(geometricUnit),
          length: GeckoMeasurement.CreateFromValue(
            length,
            GeckoMeasurementUnit.Meters
          ).convertTo(geometricUnit),
          axialPosition: GeckoMeasurement.CreateFromValue(
            axialPosition,
            GeckoMeasurementUnit.Meters
          ).convertTo(geometricUnit),
          depth: GeckoMeasurement.CreateFromValue(
            depth,
            GeckoMeasurementUnit.Meters
          ).convertTo(geometricUnit),
          height: GeckoMeasurement.CreateFromValue(
            height,
            GeckoMeasurementUnit.Meters
          ).convertTo(geometricUnit),
          amplitude: Number(amplitude.toFixed(1)),
          classification: indication.customInfo.damage_mechanism || 'Unknown',
          typeOfIndication: indication.customInfo.damage_mechanism || 'Unknown',
        }
      })

      return {
        ...scan,
        indications: mappedIndications,
      }
    })
  }, [formData.weldScanData, flatWeldIndications, geometricUnit])

  // Check if we have weld scan data (user has filled out the form)
  const hasWeldScanData = scansWithIndications.length > 0
  const hasIndications = scansWithIndications.some(
    (scan) => scan.totalIndications > 0
  )
  const hasVisualizations = weldIndicationsWithImages.length > 0

  // Don't render if no phased-array inspection exists or no content
  if (!hasPhasedArrayInspection || (!hasWeldScanData && !hasVisualizations)) {
    return null
  }

  return (
    <>
      {/* Weld Scan Summary Table */}
      {hasWeldScanData && (
        <Page
          size='A4'
          orientation='landscape'
          style={PDFStyles.page}
          id='weldScanSummary'>
          <Text
            render={({ pageNumber }) => {
              onRender({ id: 'weldScanIndications', pageNumber })
              return ''
            }}
          />
          <PageHeader title='PAUT Weld Examination' />
          <View style={PDFStyles.contentWithPadding}>
            <WeldScanSummaryTable scans={scansWithIndications} />
          </View>
          <PageFooter
            jobNumber={formData.coverPageData.geckoJobId}
            revisionNumber={formData.revisionMetadata.revisionNumber}
            prelim={
              !(
                formData.signoff?.reviewerSignoff &&
                formData.signoff.supervisorSignoff
              )
            }
          />
        </Page>
      )}

      {hasWeldScanData && hasIndications && (
        <Page size='A4' orientation='landscape' style={PDFStyles.page} wrap>
          <PageHeader title='PAUT Weld Examination - Indication Details' />
          <View style={PDFStyles.contentWithPadding}>
            <WeldIndicationsTable scans={scansWithIndications} />
          </View>
          <PageFooter
            jobNumber={formData.coverPageData.geckoJobId}
            revisionNumber={formData.revisionMetadata.revisionNumber}
            prelim={
              !(
                formData.signoff?.reviewerSignoff &&
                formData.signoff.supervisorSignoff
              )
            }
          />
        </Page>
      )}

      <WeldIndicationVisualizations
        weldIndicationsWithImages={weldIndicationsWithImages}
        renderedWeldImageUrls={renderedWeldImageUrls}
        formData={formData}
        fileNamesWithoutIndications={fileNamesWithoutIndications}
      />
    </>
  )
}

export const WeldScanIndications: ReportSection = {
  RenderReact,
  RenderPDF,
  required: false,
  id: 'weldScanIndications',
  title: 'PAUT Weld Examination',
  description: 'PAUT weld scan data and indications',
  requiredFields: [],
}
